/**
 * Admin API client functions for User Management
 * Provides functions for fetching users, updating roles, etc.
 */
import { apiOrigin } from '@/lib/config'
import { ApiError } from './client'
import type {
  ListUsersRequest,
  ListUsersResponse,
  UserDetail,
  RoleInfo,
  RoleCode,
  CurrentUser,
} from '@/types/admin'
import { apiFetch } from './http'

/**
 * Get the orchestrator API base URL from config
 * Ensures trailing slash for correct URL resolution with new URL()
 */
function getOrchestratorUrl(): string {
  // Absolute, because the callers below build URLs with `new URL(path, base)` and a relative base throws.
  // Behind the BFF the configured origin is empty, which means *this* origin — same-origin is the point
  // (checklist D5), and an absolute URL to the page's own origin is still same-origin.
  return `${apiOrigin() || window.location.origin}/`
}

// ============================================================================
// Query Keys
// ============================================================================

/**
 * Query key factory for admin API
 */
export const adminKeys = {
  all: ['admin'] as const,
  users: () => ['admin', 'users'] as const,
  userList: (params: ListUsersRequest) => ['admin', 'users', 'list', params] as const,
  userDetail: (oid: string) => ['admin', 'users', 'detail', oid] as const,
  roles: () => ['admin', 'roles'] as const,
  currentUser: () => ['admin', 'currentUser'] as const,
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Fetch paginated list of users
 */
export async function fetchUsers(params: ListUsersRequest): Promise<ListUsersResponse> {
  const baseUrl = getOrchestratorUrl()
  const url = new URL('api/admin/users', baseUrl)
  
  url.searchParams.set('limit', String(params.limit))
  url.searchParams.set('offset', String(params.offset))
  
  if (params.search) {
    url.searchParams.set('search', params.search)
  }
  if (params.role) {
    url.searchParams.set('role', params.role)
  }
  if (params.sortBy) {
    url.searchParams.set('sortBy', params.sortBy)
  }
  if (params.sortDirection) {
    url.searchParams.set('sortDirection', params.sortDirection)
  }

  const response = await apiFetch(url.toString())

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `HTTP error ${response.status}`,
      response.status,
      response.statusText,
      errorData
    )
  }

  return response.json()
}

/**
 * Fetch a single user by OID
 */
export async function fetchUser(oid: string): Promise<UserDetail> {
  const baseUrl = getOrchestratorUrl()
  const url = new URL(`api/admin/users/${oid}`, baseUrl)

  const response = await apiFetch(url.toString())

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `HTTP error ${response.status}`,
      response.status,
      response.statusText,
      errorData
    )
  }

  return response.json()
}

/**
 * Update a user's role
 */
export async function updateUserRole(
  oid: string,
  role: RoleCode,
  reason?: string
): Promise<void> {
  const baseUrl = getOrchestratorUrl()
  const url = new URL(`api/admin/users/${oid}/role`, baseUrl)

  const body: { role: RoleCode; reason?: string } = { role }
  if (reason !== undefined) {
    body.reason = reason
  }

  const response = await apiFetch(url.toString(), {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `HTTP error ${response.status}`,
      response.status,
      response.statusText,
      errorData
    )
  }

  return response.json()
}

/**
 * Fetch all available roles
 */
export async function fetchRoles(): Promise<RoleInfo[]> {
  const baseUrl = getOrchestratorUrl()
  const url = new URL('api/admin/roles', baseUrl)

  const response = await apiFetch(url.toString())

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `HTTP error ${response.status}`,
      response.status,
      response.statusText,
      errorData
    )
  }

  return response.json()
}

/**
 * Fetch current authenticated user info
 */
export async function fetchCurrentUser(): Promise<CurrentUser> {
  const baseUrl = getOrchestratorUrl()
  const url = new URL('api/me', baseUrl)

  const response = await apiFetch(url.toString())

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `HTTP error ${response.status}`,
      response.status,
      response.statusText,
      errorData
    )
  }

  return response.json()
}

// ============================================================================
// Budget Admin API Functions
// ============================================================================

import type {
  ListUserBudgetsRequest,
  ListUserBudgetsResponse,
  BudgetRoleInfo,
  SetUserBudgetOverrideRequest,
  UpdateRoleBudgetRequest,
} from '@/types/budgetAdmin'

/**
 * Query key factory for budget admin API
 */
export const budgetAdminKeys = {
  all: ['admin', 'budget'] as const,
  users: () => ['admin', 'budget', 'users'] as const,
  userList: (params: ListUserBudgetsRequest) => ['admin', 'budget', 'users', 'list', params] as const,
  roles: () => ['admin', 'budget', 'roles'] as const,
}

/**
 * Fetch paginated list of user budgets
 */
export async function fetchUserBudgets(
  params: ListUserBudgetsRequest = {}
): Promise<ListUserBudgetsResponse> {
  const baseUrl = getOrchestratorUrl()
  const url = new URL('api/admin/budget/users', baseUrl)

  if (params.page) {
    url.searchParams.set('page', String(params.page))
  }
  if (params.pageSize) {
    url.searchParams.set('pageSize', String(params.pageSize))
  }

  const response = await apiFetch(url.toString())

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `HTTP error ${response.status}`,
      response.status,
      response.statusText,
      errorData
    )
  }

  return response.json()
}

/**
 * Fetch all budget roles
 */
export async function fetchBudgetRoles(): Promise<BudgetRoleInfo[]> {
  const baseUrl = getOrchestratorUrl()
  const url = new URL('api/admin/budget/roles', baseUrl)

  const response = await apiFetch(url.toString())

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `HTTP error ${response.status}`,
      response.status,
      response.statusText,
      errorData
    )
  }

  return response.json()
}

/**
 * Set a user's budget override
 */
export async function setUserBudgetOverride(
  oid: string,
  request: SetUserBudgetOverrideRequest
): Promise<void> {
  const baseUrl = getOrchestratorUrl()
  const url = new URL(`api/admin/budget/users/${oid}/override`, baseUrl)

  const response = await apiFetch(url.toString(), {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `HTTP error ${response.status}`,
      response.status,
      response.statusText,
      errorData
    )
  }
}

/**
 * Remove a user's budget override
 */
export async function removeUserBudgetOverride(oid: string): Promise<void> {
  const baseUrl = getOrchestratorUrl()
  const url = new URL(`api/admin/budget/users/${oid}/override`, baseUrl)

  const response = await apiFetch(url.toString(), {
    method: 'DELETE',
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `HTTP error ${response.status}`,
      response.status,
      response.statusText,
      errorData
    )
  }
}

/**
 * Update a role's budget limit
 */
export async function updateRoleBudget(
  roleId: string,
  request: UpdateRoleBudgetRequest
): Promise<void> {
  const baseUrl = getOrchestratorUrl()
  const url = new URL(`api/admin/budget/roles/${roleId}`, baseUrl)

  const response = await apiFetch(url.toString(), {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `HTTP error ${response.status}`,
      response.status,
      response.statusText,
      errorData
    )
  }
}
